import { Group, Stack, Text, UnstyledButton } from '@mantine/core';

interface TablePaginationProps {
  label: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  bordered?: boolean;
}

const arrowButtonStyle = (disabled: boolean) => ({
  width: 32,
  height: 32,
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: 6,
  border: '1px solid var(--color-border-default)',
  backgroundColor: 'var(--color-bg-surface)',
  color: 'var(--color-text-secondary)',
  padding: 0,
  fontSize: 16,
  lineHeight: 1,
  opacity: disabled ? 0.5 : 1
});

const pageNumberStyle = (current: boolean) => ({
  width: 32,
  height: 32,
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: 6,
  border: `1px solid ${current ? 'var(--color-accent-primary)' : 'var(--color-border-default)'}`,
  backgroundColor: current ? 'var(--color-accent-primary)' : 'var(--color-bg-surface)',
  color: current ? 'var(--color-text-on-accent)' : 'var(--color-text-secondary)',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: 1,
  padding: 0
});

interface PaginationButtonsProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  gap?: number;
}

const MAX_VISIBLE_PAGES = 3;

// Prozor od najviše MAX_VISIBLE_PAGES brojeva, usidren na trenutnu stranicu kao
// lijevi rub (ograničen da ne prijeđe totalPages) — npr. stranica 1 od 15 prikaže
// [1,2,3], stranica 3 prikaže [3,4,5], zadnje stranice ostaju [totalPages-2..totalPages].
const getVisiblePages = (page: number, totalPages: number) => {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const start = Math.min(Math.max(page, 1), totalPages - MAX_VISIBLE_PAGES + 1);
  return Array.from({ length: MAX_VISIBLE_PAGES }, (_, i) => start + i);
};

// Izdvojeno iz TablePagination da ListPagination može posložiti page-size
// selector ispred ovih gumba u istom retku, bez oznake (label ide zasebno).
export const PaginationButtons = ({
  page,
  totalPages,
  onPageChange,
  gap = 8
}: PaginationButtonsProps) => (
  <Group gap={gap}>
    <UnstyledButton
      onClick={() => onPageChange(Math.max(1, page - 1))}
      disabled={page === 1}
      style={arrowButtonStyle(page === 1)}
    >
      ‹
    </UnstyledButton>
    {getVisiblePages(page, totalPages).map((number) => (
      <UnstyledButton
        key={number}
        onClick={() => onPageChange(number)}
        style={pageNumberStyle(number === page)}
      >
        {number}
      </UnstyledButton>
    ))}
    <UnstyledButton
      onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      disabled={page === totalPages}
      style={arrowButtonStyle(page === totalPages)}
    >
      ›
    </UnstyledButton>
  </Group>
);

export const TablePagination = ({
  label,
  page,
  totalPages,
  onPageChange,
  bordered = true
}: TablePaginationProps) => {
  const buttons = (
    <PaginationButtons
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
      gap={bordered ? 8 : 6}
    />
  );

  const labelText = (
    <Text
      fz={bordered ? 11.5 : 12}
      ta={bordered ? undefined : 'center'}
      style={{ color: 'var(--color-text-muted)' }}
    >
      {label}
    </Text>
  );

  if (!bordered) {
    return (
      <Stack gap={20} align="center" style={{ padding: '8px 0', width: '100%' }}>
        {labelText}
        {buttons}
      </Stack>
    );
  }

  return (
    <Group
      justify="space-between"
      style={{ padding: '14px 20px', borderTop: '1px solid var(--color-border-soft)' }}
    >
      {labelText}
      {buttons}
    </Group>
  );
};
